(function (root, factory) {
  const core = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = core;
  }

  root.NeverTapFitCore = core;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const normalize = (value) =>
    String(value == null ? '' : value)
      .trim()
      .toLocaleUpperCase('de-DE')
      .replace(/\s+/g, ' ');

  const csv = (value) =>
    String(value || '')
      .split(',')
      .map(normalize)
      .filter(Boolean);

  const number = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  function resolveProfile(profiles, product) {
    const type = normalize(product && product.type);
    const tags = (product && product.tags ? product.tags : []).map(normalize);

    return (
      (profiles || []).find((profile) => {
        if (!profile || profile.enabled === false) return false;

        const productTypes = csv(profile.productTypes);
        const profileTags = csv(profile.tags);
        const typeMatch = productTypes.length > 0 && productTypes.includes(type);
        const tagMatch = profileTags.length > 0 && profileTags.some((tag) => tags.includes(tag));

        return typeMatch || tagMatch;
      }) || null
    );
  }

  function getSizeOptionIndex(product, configuredNames) {
    const accepted = csv(configuredNames);
    return (product.optionNames || []).findIndex((name) => accepted.includes(normalize(name)));
  }

  function getOrderedSizes(product, sizeOptionIndex) {
    const configuredOption = (product.optionValues || [])[sizeOptionIndex];
    const ordered = configuredOption && Array.isArray(configuredOption.values) ? configuredOption.values : [];
    const fallback = (product.variants || []).map((variant) => (variant.options || [])[sizeOptionIndex]);
    const unique = [];

    ordered.concat(fallback).forEach((size) => {
      if (size && !unique.some((value) => normalize(value) === normalize(size))) unique.push(size);
    });

    return unique;
  }

  function isCompleteRule(rule) {
    const minHeight = number(rule.minHeight);
    const maxHeight = number(rule.maxHeight);
    const minWeight = number(rule.minWeight);
    const maxWeight = number(rule.maxWeight);

    return (
      rule &&
      rule.enabled === true &&
      normalize(rule.profileKey) !== '' &&
      normalize(rule.size) !== '' &&
      minHeight > 0 &&
      maxHeight >= minHeight &&
      minWeight > 0 &&
      maxWeight >= minWeight
    );
  }

function scoreAxis(value, min, max) {
  min = number(min);
  max = number(max);

  const span = Math.max(max - min, 1);
  const midpoint = (min + max) / 2;

  /*
   * Wert liegt innerhalb des hinterlegten Bereichs.
   *
   * Auch innerhalb eines Bereichs gibt es einen kleinen Unterschied:
   * Je näher am Mittelpunkt, desto besser der Score.
   *
   * Wichtig:
   * Dieser Einfluss bleibt bewusst klein.
   */
  if (value >= min && value <= max) {
    return (Math.abs(value - midpoint) / span) * 0.2;
  }

  /*
   * Außerhalb des Bereichs beginnt eine größere Strafwertung.
   *
   * Dadurch kann z. B.:
   *
   * Körpergröße -> S
   * Gewicht     -> M
   *
   * trotzdem sauber zwischen S und M verglichen werden.
   */
  if (value < min) {
    return 0.35 + (min - value) / span;
  }

  return 0.35 + (value - max) / span;
}

function getProfileWeights(profile) {
  const key = normalize(profile && profile.key);

  /*
   * MMA Shorts:
   * Gewicht ist etwas wichtiger als Körpergröße.
   */
  if (key === 'MMA_SHORTS') {
    return {
      height: 0.35,
      weight: 0.65,
    };
  }

  /*
   * Rashguards und Standard:
   * Gewicht hat ebenfalls etwas mehr Einfluss.
   */
  return {
    height: 0.4,
    weight: 0.6,
  };
}

function scoreRule(rule, height, weight, weights) {
  const heightScore = scoreAxis(
    height,
    rule.minHeight,
    rule.maxHeight
  );

  const weightScore = scoreAxis(
    weight,
    rule.minWeight,
    rule.maxWeight
  );

  return {
    heightScore,
    weightScore,
    total:
      heightScore * weights.height +
      weightScore * weights.weight,
  };
}

  function buildAvailability(product, sizeOptionIndex, orderedSizes) {
    return orderedSizes.map((size) => {
      const variants = (product.variants || []).filter(
        (variant) => normalize((variant.options || [])[sizeOptionIndex]) === normalize(size)
      );

      return {
        size,
        available: variants.some((variant) => variant.available === true),
        variantIds: variants.map((variant) => variant.id),
      };
    });
  }

  function findRuleForSize(rules, size) {
    return rules.find((rule) => normalize(rule.size) === normalize(size));
  }

  function recommend(config, answers, profile) {
  const product = config.product || {};

  const sizeOptionIndex = getSizeOptionIndex(
    product,
    config.ui && config.ui.sizeOptionNames
  );

  if (sizeOptionIndex < 0) {
    return {
      status: 'missing_size_option',
    };
  }

  const orderedSizes = getOrderedSizes(
    product,
    sizeOptionIndex
  );

  const profileRules = (config.rules || []).filter(
    (rule) =>
      isCompleteRule(rule) &&
      normalize(rule.profileKey) === normalize(profile.key)
  );

  /*
   * Mindestens zwei Größen brauchen wir,
   * damit eine sinnvolle Größenberechnung möglich ist.
   */
  if (profileRules.length < 2) {
    return {
      status: 'unconfigured',
      orderedSizes,
      sizeOptionIndex,
    };
  }

  const height = number(answers.height);
  const weight = number(answers.weight);

  const weights = getProfileWeights(profile);

  /*
   * ---------------------------------------------------------
   * 1. JEDE GRÖSSE BEWERTEN
   * ---------------------------------------------------------
   *
   * Es gibt ab jetzt KEIN:
   *
   * "Körpergröße UND Gewicht müssen beide im selben Bereich sein"
   *
   * mehr.
   *
   * Stattdessen erhält jede Größe einen Score.
   */
  let scored = profileRules
    .map((rule) => {
      const index = orderedSizes.findIndex(
        (size) =>
          normalize(size) === normalize(rule.size)
      );

      const scores = scoreRule(
        rule,
        height,
        weight,
        weights
      );

      return {
        rule,
        index,
        heightScore: scores.heightScore,
        weightScore: scores.weightScore,
        total: scores.total,
      };
    })
    /*
     * Regeln für Größen, die das Produkt gar nicht verkauft,
     * werden nicht berücksichtigt.
     */
    .filter((entry) => entry.index >= 0)
    .sort((a, b) => a.total - b.total);

  if (scored.length === 0) {
    return {
      status: 'size_not_sold',
      orderedSizes,
      sizeOptionIndex,
    };
  }

  /*
   * ---------------------------------------------------------
   * 2. BESTE GRÖSSE NACH KÖRPERGRÖSSE UND GEWICHT
   * ---------------------------------------------------------
   *
   * Damit erkennen wir Fälle wie:
   *
   * Körpergröße -> S
   * Gewicht     -> M
   */
  const heightBest = [...scored].sort(
    (a, b) => a.heightScore - b.heightScore
  )[0];

  const weightBest = [...scored].sort(
    (a, b) => a.weightScore - b.weightScore
  )[0];

  const measurementSplit =
    heightBest &&
    weightBest &&
    heightBest.index !== weightBest.index;

  const measurementGap =
    heightBest && weightBest
      ? Math.abs(heightBest.index - weightBest.index)
      : 0;

  /*
   * Wenn z. B.:
   *
   * Körpergröße -> S
   * Gewicht     -> XL
   *
   * dann handelt es sich nicht einfach um einen normalen
   * Grenzfall zwischen zwei Größen.
   */
  const strongMeasurementConflict =
    measurementGap > 1;

  /*
   * ---------------------------------------------------------
   * 3. PRIMÄRE EMPFEHLUNG
   * ---------------------------------------------------------
   */
  let primary = scored[0];

  /*
   * ---------------------------------------------------------
   * 4. BESTE BENACHBARTE GRÖSSE SUCHEN
   * ---------------------------------------------------------
   *
   * Nur direkte Nachbarn sind erlaubt:
   *
   * S <-> M
   * M <-> L
   * L <-> XL
   *
   * Niemals beispielsweise S + L.
   */
  let secondary = scored
    .filter(
      (entry) =>
        Math.abs(entry.index - primary.index) === 1
    )
    .sort((a, b) => a.total - b.total)[0] || null;

  const scoreGap = secondary
    ? Math.abs(secondary.total - primary.total)
    : Infinity;

  /*
   * Eine zweite Größe zeigen wir, wenn:
   *
   * 1. die Scores relativ nah beieinander liegen
   *
   * ODER
   *
   * 2. Körpergröße und Gewicht eindeutig auf zwei
   *    benachbarte Größen deuten.
   */
  const secondaryIsMeasurementCandidate =
    Boolean(
      secondary &&
        measurementSplit &&
        (
          secondary.index === heightBest.index ||
          secondary.index === weightBest.index
        )
    );

  const secondaryPlausible =
    Boolean(
      secondary &&
        !strongMeasurementConflict &&
        (
          scoreGap <= 0.35 ||
          secondaryIsMeasurementCandidate
        )
    );

  if (!secondaryPlausible) {
    secondary = null;
  }

  /*
   * ---------------------------------------------------------
   * 5. PASSFORM UND KÖRPERBAU
   * ---------------------------------------------------------
   *
   * Sehr wichtig:
   *
   * "Sehr eng" bedeutet NICHT mehr automatisch:
   * eine Größe kleiner.
   *
   * "Locker" bedeutet NICHT mehr automatisch:
   * eine Größe größer.
   *
   * Der Fit entscheidet nur zwischen zwei Größen,
   * die sowieso körperlich plausibel sind.
   */
  if (secondary) {
    const smaller =
      primary.index < secondary.index
        ? primary
        : secondary;

    const larger =
      primary.index > secondary.index
        ? primary
        : secondary;

    let preferred = null;

    /*
     * Gewünschte Passform hat höchste Priorität
     * beim Grenzfall.
     */
    if (answers.fit === 'tight') {
      preferred = smaller;
    } else if (answers.fit === 'loose') {
      preferred = larger;
    }

    /*
     * Bei normaler Passform kann der Körperbau
     * als leichter Tie-Breaker verwendet werden.
     */
    if (!preferred && answers.fit === 'normal') {
      if (answers.body === 'slim') {
        preferred = smaller;
      }

      if (answers.body === 'strong') {
        preferred = larger;
      }
    }

    /*
     * Nur tauschen, wenn beide Größen tatsächlich
     * plausibel sind.
     */
    if (preferred && preferred !== primary) {
      const oldPrimary = primary;
      primary = preferred;
      secondary = oldPrimary;
    }
  }

  /*
   * ---------------------------------------------------------
   * 6. PRÜFEN, OB ANGABEN AUSSERHALB DER REGELDATEN LIEGEN
   * ---------------------------------------------------------
   *
   * Anders als vorher liefern wir TROTZDEM eine Empfehlung.
   */
  const minHeight = Math.min(
    ...profileRules.map((rule) =>
      number(rule.minHeight)
    )
  );

  const maxHeight = Math.max(
    ...profileRules.map((rule) =>
      number(rule.maxHeight)
    )
  );

  const minWeight = Math.min(
    ...profileRules.map((rule) =>
      number(rule.minWeight)
    )
  );

  const maxWeight = Math.max(
    ...profileRules.map((rule) =>
      number(rule.maxWeight)
    )
  );

  const outsideSupportedEnvelope =
    height < minHeight ||
    height > maxHeight ||
    weight < minWeight ||
    weight > maxWeight;

  /*
   * ---------------------------------------------------------
   * 7. CONFIDENCE
   * ---------------------------------------------------------
   */
  let confidence = 'clear';

  if (
    outsideSupportedEnvelope ||
    strongMeasurementConflict
  ) {
    confidence = 'low';
  } else if (secondary) {
    confidence = 'between';
  }

  /*
   * ---------------------------------------------------------
   * 8. VERFÜGBARKEIT
   * ---------------------------------------------------------
   */
  const availability = buildAvailability(
    product,
    sizeOptionIndex,
    orderedSizes
  );

  const idealSize = primary.rule.size;

  const idealAvailability = availability.find(
    (entry) =>
      normalize(entry.size) === normalize(idealSize)
  );

  const nearbySize = secondary
    ? secondary.rule.size
    : null;

  const nearbyAvailability = nearbySize
    ? availability.find(
        (entry) =>
          normalize(entry.size) ===
          normalize(nearbySize)
      )
    : null;

  const nearbyFit =
    secondary
      ? secondary.index < primary.index
        ? 'tighter'
        : 'looser'
      : null;

  /*
   * ---------------------------------------------------------
   * 9. AUSVERKAUFTE GRÖSSE
   * ---------------------------------------------------------
   */
  const alternatives = [];

  const addAlternative = (size, fit) => {
    if (!size) return;

    if (
      alternatives.some(
        (alternative) =>
          normalize(alternative.size) ===
          normalize(size)
      )
    ) {
      return;
    }

    alternatives.push({
      size,
      fit,
    });
  };

  /*
   * Wenn unsere zweite reguläre Empfehlung verfügbar ist,
   * darf sie bei ausverkaufter Hauptgröße angeboten werden.
   */
  if (
    !idealAvailability ||
    !idealAvailability.available
  ) {
    if (
      nearbyAvailability &&
      nearbyAvailability.available
    ) {
      addAlternative(
        nearbyAvailability.size,
        nearbyFit
      );
    }

    /*
     * Die alten allowSmaller / allowLarger Optionen
     * bleiben als Sicherheitsnetz für weitere
     * Lagerbestands-Alternativen erhalten.
     */
    const idealIndex = primary.index;
    const idealRule = primary.rule;

    const smaller =
      availability[idealIndex - 1];

    const larger =
      availability[idealIndex + 1];

    if (
      idealRule.allowSmaller === true &&
      smaller &&
      smaller.available
    ) {
      addAlternative(
        smaller.size,
        'tighter'
      );
    }

    if (
      idealRule.allowLarger === true &&
      larger &&
      larger.available
    ) {
      addAlternative(
        larger.size,
        'looser'
      );
    }
  }

  /*
   * ---------------------------------------------------------
   * 10. DIAGNOSEWERTE
   * ---------------------------------------------------------
   *
   * Diese sieht der Kunde nicht.
   * Sie können später aber für Analytics hilfreich sein.
   */
  const scores = {};

  scored.forEach((entry) => {
    scores[entry.rule.size] =
      Number(entry.total.toFixed(4));
  });

  let reason = 'clear';

  if (outsideSupportedEnvelope) {
    reason = 'outside_rules';
  } else if (strongMeasurementConflict) {
    reason = 'measurement_conflict';
  } else if (measurementSplit) {
    reason = 'height_weight_split';
  } else if (secondary) {
    reason = 'close_scores';
  }

  return {
    status: 'ok',

    baseSize: scored[0].rule.size,

    idealSize,

    available: Boolean(
      idealAvailability &&
        idealAvailability.available
    ),

    alternatives,

    confidence,

    nearbySize,

    nearbyAvailable: Boolean(
      nearbyAvailability &&
        nearbyAvailability.available
    ),

    nearbyFit,

    measurementSplit,

    reason,

    matchMode:
      outsideSupportedEnvelope
        ? 'extrapolated'
        : 'score',

    scoreGap:
      Number.isFinite(scoreGap)
        ? Number(scoreGap.toFixed(4))
        : null,

    scores,

    sizeOptionIndex,

    orderedSizes,
  };
}

  return {
    normalize,
    resolveProfile,
    getSizeOptionIndex,
    getOrderedSizes,
    isCompleteRule,
    recommend,
  };
});
